import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageSquare, Mail, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIONS = [
    { key: "call",  label: "Call",  Icon: Phone },
    { key: "text",  label: "Text",  Icon: MessageSquare },
    { key: "email", label: "Email", Icon: Mail },
];

const ICON_BY_METHOD = { call: Phone, text: MessageSquare, email: Mail };

function timeAgo(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const ms = Date.now() - then;
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    return `${h}h ago`;
}

/**
 * Per-lead outreach tracker. Three tap-cards (Call / Text / Email).
 * Each shows today's count (resets at midnight) + all-time total.
 * Tapping a card logs a touch via POST /api/coach/leads/{user_id}/outreach.
 *
 * Only rendered when lead.status === "lead".
 */
export default function LeadOutreachTracker({ leadUserId, onLogged }) {
    const [counts, setCounts] = useState({ today: { call: 0, text: 0, email: 0 }, total: { call: 0, text: 0, email: 0 }, today_log: [] });
    const [loading, setLoading] = useState(true);
    const [logging, setLogging] = useState(null); // which action is in-flight

    useEffect(() => {
        let cancel = false;
        (async () => {
            setLoading(true);
            try {
                const r = await axios.get(`${API}/coach/leads/${leadUserId}/outreach`, { withCredentials: true });
                if (!cancel) setCounts({ today: r.data.today, total: r.data.total, today_log: r.data.today_log || [] });
            } catch (e) {
                if (!cancel) toast.error("Couldn't load outreach", { description: e?.response?.data?.detail });
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [leadUserId]);

    const log = async (action) => {
        setLogging(action);
        try {
            const r = await axios.post(
                `${API}/coach/leads/${leadUserId}/outreach`,
                { action },
                { withCredentials: true },
            );
            setCounts({ today: r.data.today, total: r.data.total, today_log: r.data.today_log || [] });
            onLogged?.(action);
        } catch (e) {
            toast.error("Couldn't log touch", { description: e?.response?.data?.detail });
        } finally {
            setLogging(null);
        }
    };

    return (
        <div data-testid="lead-outreach-tracker" className="p-6 border-b border-zinc-900">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3">Outreach today</div>
            <div className="grid grid-cols-3 gap-2">
                {ACTIONS.map(({ key, label, Icon }) => {
                    const t = counts.today[key] || 0;
                    const all = counts.total[key] || 0;
                    const isLogging = logging === key;
                    return (
                        <button
                            type="button"
                            key={key}
                            data-testid={`outreach-${key}`}
                            disabled={isLogging || loading}
                            onClick={() => log(key)}
                            className={`group border bg-black/40 hover:bg-brand-red/10 hover:border-brand-red transition-colors px-3 py-3 text-left disabled:opacity-50 ${t > 0 ? "border-brand-red/60" : "border-zinc-800"}`}
                            title={`Log a ${label.toLowerCase()} — today ${t}, total ${all}`}
                        >
                            <div className="flex items-center gap-2 mb-2">
                                {isLogging ? (
                                    <Loader2 className="w-3.5 h-3.5 text-brand-red animate-spin" />
                                ) : (
                                    <Icon className={`w-3.5 h-3.5 ${t > 0 ? "text-brand-red" : "text-zinc-400 group-hover:text-brand-red"}`} />
                                )}
                                <span className="text-[10px] tracking-[0.2em] uppercase text-white font-semibold">{label}</span>
                            </div>
                            <div className="flex items-baseline gap-1.5">
                                <span data-testid={`outreach-${key}-today`} className={`font-display text-2xl tracking-wider ${t > 0 ? "text-brand-red" : "text-white"}`}>{t}</span>
                                <span className="text-[9px] tracking-[0.2em] uppercase text-zinc-400">today</span>
                            </div>
                            <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-300 mt-1">
                                Total <span data-testid={`outreach-${key}-total`} className="text-zinc-100 font-semibold">{all}</span>
                            </div>
                        </button>
                    );
                })}
            </div>
            {counts.today_log.length > 0 && (
                <div data-testid="outreach-today-log" className="mt-4">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-2">Today's touches</div>
                    <ul className="space-y-1.5">
                        {counts.today_log.map((entry, i) => {
                            const Icon = ICON_BY_METHOD[entry.method] || MessageSquare;
                            return (
                                <li
                                    key={`${entry.at}-${i}`}
                                    data-testid={`outreach-log-row-${i}`}
                                    className="flex items-center gap-2 text-xs text-zinc-200 bg-black/40 border border-zinc-900 px-2.5 py-1.5"
                                >
                                    <Icon className="w-3 h-3 text-brand-red flex-shrink-0" />
                                    <span className="capitalize text-white font-medium">{entry.method}</span>
                                    <span className="text-zinc-400">by</span>
                                    <span className="text-white truncate">{entry.by_name}</span>
                                    <span className="text-zinc-500 ml-auto whitespace-nowrap">{timeAgo(entry.at)}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
